/**
 * Label Renderer Utilities
 *
 * Shared utilities for rendering labels from templates.
 * Used by both the editor preview and the actual print output.
 *
 * Key features:
 * - mm-to-pixel coordinate conversion
 * - Component filtering and sorting
 * - Label type determination from fulfillment type
 */

import type {
  ComponentConfig,
  CanvasSize,
  LabelType,
} from '@/features/production-settings/components/label-layout/label-layout-types';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Millimeters per inch (standard conversion) */
export const MM_PER_INCH = 25.4;

/** CSS pixels per inch (CSS spec defines 1in = 96px) */
export const CSS_PPI = 96;

/** Pixels per millimeter at 96 DPI (~3.78) */
export const PX_PER_MM = CSS_PPI / MM_PER_INCH;

// ============================================================================
// TYPES
// ============================================================================

export interface LabelRenderOptions {
  /** Scale factor (default 1 for print - no scaling) */
  scale?: number;
  /** Show debug borders around components */
  showBorders?: boolean;
}

/**
 * Garment data structure for label rendering.
 * Matches the data shape used by GarmentLabel and print flows.
 */
export interface GarmentData {
  id: string;
  orderNumber: string;
  sku: string;
  shopifySku?: string | null;
  productName: string;
  size: string;
  color: string;
  requiresPretreat: boolean;
  fulfillmentType: 'POD' | 'P&P';
  itemNumber: number;
  totalItems: number;
  binLabel: string;
  isRedo: boolean;
  shopifyProductType: string | null;
  /**
   * Print technology axis ('DTG' | 'DTF'). Phase 50.01.3 / BLU-233: drives the
   * QR `m` field AND the on-label print-method indicator (DTF gets a distinct
   * semantic colour, NOT DTG's plain black box). Optional for back-compat —
   * callers that don't supply it default to 'DTG'.
   */
  printTechnology?: 'DTG' | 'DTF';
}

// ============================================================================
// COORDINATE CONVERSION
// ============================================================================

/**
 * Calculate pixel position/size from mm-based component config.
 * Used by both editor preview and print output.
 */
export function getComponentPixelBounds(
  config: ComponentConfig,
  scale: number = 1
): { x: number; y: number; width: number; height: number } {
  return {
    x: config.position.x * PX_PER_MM * scale,
    y: config.position.y * PX_PER_MM * scale,
    width: config.size.width * PX_PER_MM * scale,
    height: config.size.height * PX_PER_MM * scale,
  };
}

/**
 * Get canvas dimensions in pixels.
 */
export function getCanvasPixelSize(
  canvasSize: CanvasSize,
  scale: number = 1
): { width: number; height: number } {
  return {
    width: canvasSize.width * PX_PER_MM * scale,
    height: canvasSize.height * PX_PER_MM * scale,
  };
}

// ============================================================================
// COMPONENT UTILITIES
// ============================================================================

/**
 * Filter visible components and sort by zIndex for proper layering.
 */
export function getVisibleComponents(components: ComponentConfig[]): ComponentConfig[] {
  return components
    .filter((c) => c.visible)
    .sort((a, b) => (a.zIndex ?? 1) - (b.zIndex ?? 1));
}

/**
 * Determine label type from fulfillment type string.
 * Handles legacy data and fallbacks.
 */
export function getLabelTypeFromFulfillment(fulfillmentType: string): LabelType {
  // Normalize and check
  const normalized = fulfillmentType?.toUpperCase() ?? '';

  if (normalized === 'POD' || normalized === 'PRINT ON DEMAND') {
    return 'POD';
  }
  if (normalized === 'P&P' || normalized === 'PICK AND PACK' || normalized === 'PICK & PACK') {
    return 'P&P';
  }

  // Default to POD for unknown types
  return 'POD';
}

// ============================================================================
// TEMPLATE MATCHING
// ============================================================================

/**
 * Find the best matching template for a given label type and canvas size.
 */
export function findMatchingTemplate<
  T extends { labelType: LabelType; canvasSize: CanvasSize; isDefault: boolean; isBuiltIn: boolean }
>(
  templates: T[],
  labelType: LabelType,
  preferredCanvasSize?: CanvasSize
): T | null {
  const matchesCriteria = (t: T) => {
    const typeMatches = t.labelType === labelType || t.labelType === 'BOTH';
    const sizeMatches = preferredCanvasSize
      ? t.canvasSize.width === preferredCanvasSize.width &&
        t.canvasSize.height === preferredCanvasSize.height
      : true;
    return typeMatches && sizeMatches && t.isDefault;
  };

  // Prefer user-created default over built-in default
  const userDefault = templates.find((t) => matchesCriteria(t) && !t.isBuiltIn);
  if (userDefault) return userDefault;

  const builtInDefault = templates.find((t) => matchesCriteria(t));
  if (builtInDefault) return builtInDefault;

  // Fall back to any template matching the type
  const typeMatch = templates.find(
    (t) => t.labelType === labelType || t.labelType === 'BOTH'
  );

  if (typeMatch) return typeMatch;

  // Last resort: return first template
  return templates[0] ?? null;
}
